'use client'

import * as React from 'react'
import { useDropzone } from 'react-dropzone'
import { IoCloudUploadOutline } from 'react-icons/io5'

import type { Locale } from '@helper/utils/types'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
} from '../components'

interface Props {
  handleImageUploadAction: (image: File) => void | Promise<void>
  placeholderName: string
  initialImageUrl?: string | null
  size?: number
  className?: string
  buttonClassName?: string
  disabled?: boolean
  customTitle?: string
  maxFileSizeMB?: number
  acceptedFileTypes?: string[]
  onError?: (error: string) => void
  profile?: 'user' | 'organization'
  locale?: Locale
}

const DEFAULT_MAX_FILE_SIZE_MB = 5
const DEFAULT_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']

export function ProfileUpload({
  handleImageUploadAction,
  placeholderName,
  initialImageUrl,
  size = 60,
  className,
  buttonClassName,
  disabled,
  customTitle,
  maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
  onError,
  profile = 'user',
  locale = 'id',
}: Props) {
  const [open, setOpen] = React.useState(false)
  const [localImage, setLocalImage] = React.useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [isUploading, setIsUploading] = React.useState(false)

  const memoizedPlaceholderName = React.useMemo(() => placeholderName, [placeholderName])

  // Cleanup blob URL on unmount or when local image changes
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const clearError = React.useCallback(() => {
    setError(null)
  }, [])

  const handleImage = React.useCallback(
    async (file: FileList | File[] | null) => {
      clearError()

      if (!file || file.length === 0) {
        const errorMsg = locale === 'id' ? 'Silakan unggah file gambar' : 'Please upload an image file'
        setError(errorMsg)
        onError?.(errorMsg)
        return
      }

      if (file.length > 1) {
        const errorMsg = locale === 'id' ? 'Hanya dapat memilih satu gambar' : 'Only one image can be selected'
        setError(errorMsg)
        onError?.(errorMsg)
        return
      }

      const selectedFile = file[0]

      if (!selectedFile) {
        const errorMsg = locale === 'id' ? 'Silakan pilih file' : 'Please select a file'
        setError(errorMsg)
        onError?.(errorMsg)
        return
      }

      if (!acceptedFileTypes.some((type) => selectedFile.type === type)) {
        const errorMsg =
          locale === 'id'
            ? `Format file tidak didukung. Gunakan: ${acceptedFileTypes.map((t) => t.split('/')[1]).join(', ')}`
            : `Unsupported file format. Use: ${acceptedFileTypes.map((t) => t.split('/')[1]).join(', ')}`
        setError(errorMsg)
        onError?.(errorMsg)
        return
      }

      const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024
      if (selectedFile.size > maxFileSizeBytes) {
        const errorMsg =
          locale === 'id'
            ? `Ukuran file harus ${maxFileSizeMB}MB atau lebih kecil`
            : `File size must be less than ${maxFileSizeMB}MB`
        setError(errorMsg)
        onError?.(errorMsg)
        return
      }

      try {
        setIsUploading(true)

        // Revoke previous preview URL
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl)
        }

        // Create new preview URL
        const newPreviewUrl = URL.createObjectURL(selectedFile)
        setPreviewUrl(newPreviewUrl)
        setLocalImage(selectedFile)

        await handleImageUploadAction(selectedFile)
        setOpen(false)
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : locale === 'id' ? 'Gagal mengunggah gambar' : 'Failed to upload image'
        setError(errorMsg)
        onError?.(errorMsg)
      } finally {
        setIsUploading(false)
      }
    },
    [handleImageUploadAction, maxFileSizeMB, acceptedFileTypes, previewUrl, clearError, onError],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      void handleImage(files)
    },
    accept: acceptedFileTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {} as Record<string, string[]>),
    maxFiles: 1,
    maxSize: maxFileSizeMB * 1024 * 1024,
    disabled: (disabled ?? false) || isUploading,
  })

  const displayImageUrl = React.useMemo(() => {
    if (previewUrl) return previewUrl
    if (initialImageUrl) return initialImageUrl
    return null
  }, [previewUrl, initialImageUrl])

  const buttonText = React.useMemo(() => {
    if (isUploading) return locale === 'id' ? 'Mengunggah...' : 'Uploading...'
    if (customTitle) return customTitle
    if (localImage ?? initialImageUrl) return locale === 'id' ? 'Ganti foto' : 'Change photo'
    return locale === 'id' ? 'Tambah foto' : 'Add photo'
  }, [isUploading, customTitle, localImage, initialImageUrl])

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!isUploading) {
          setOpen(newOpen)
          if (!newOpen) {
            clearError()
          }
        }
      }}
    >
      <DialogTrigger
        render={(props) => (
          <Button
            onClick={() => setOpen(true)}
            className={cn(
              'ui:w-fit ui:h-fit ui:p-0 ui:m-0 ui:bg-neutral-white ui:hover:bg-neutral-white ui:border-none ui:flex ui:flex-col ui:gap-2',
              buttonClassName,
            )}
            disabled={(disabled ?? false) || isUploading}
            aria-label={`${buttonText} untuk ${placeholderName}`}
            {...props}
          >
            <Avatar
              className={cn('ui:rounded-xl', className)}
              style={{
                width: size,
                height: size,
              }}
              profile={profile}
            >
              <AvatarImage
                src={displayImageUrl ?? undefined}
                alt={`${locale === 'id' ? 'Foto profil' : 'Profile image'} ${placeholderName}`}
              />

              <AvatarFallback
                className={cn('ui:text-lg')}
                style={{
                  width: size,
                  minHeight: size,
                  maxHeight: size,
                }}
              >
                {getInitials(memoizedPlaceholderName)}
              </AvatarFallback>
            </Avatar>

            <span className={cn('ui:text-blue-100', isUploading && 'ui:opacity-50')}>{buttonText}</span>
          </Button>
        )}
      />
      <DialogContent aria-describedby='upload-description' className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle className='ui:mb-3'>{locale === 'id' ? 'Upload Gambar' : 'Upload Image'}</DialogTitle>
          <p id='upload-description' className='ui:sr-only'>
            {locale === 'id' ? 'Dialog untuk mengunggah gambar profil' : 'Dialog for uploading profile image'}
          </p>
        </DialogHeader>

        <div {...getRootProps()} className='ui:flex ui:items-center ui:justify-center ui:w-full'>
          <label
            htmlFor='dropzone-file'
            className={cn(
              'ui:relative ui:flex ui:flex-col ui:items-center ui:justify-center ui:w-full ui:py-6 ui:border-2 ui:border-dashed ui:rounded-lg ui:cursor-pointer ui:bg-navy-10/50 ui:hover:bg-navy-10 ui:transition-colors',
              isDragActive && 'ui:border-blue-100 ui:bg-blue-20',
              error && 'ui:border-error',
              ((disabled ?? false) || isUploading) && 'ui:opacity-50 ui:cursor-not-allowed ui:hover:bg-grey-30',
              !isDragActive && !error && 'ui:border-grey-50',
            )}
          >
            <div className='ui:text-center'>
              <div
                className={cn(
                  'ui:border ui:p-2 ui:rounded-md ui:max-w-min ui:mx-auto ui:transition-colors',
                  isDragActive && 'ui:border-blue-100 ui:bg-blue-20',
                  !isDragActive && 'ui:border-grey-50',
                )}
              >
                <IoCloudUploadOutline
                  size='1.6em'
                  className={cn(isUploading && 'ui:animate-pulse', 'ui:text-blue-100')}
                />
              </div>

              <p className='ui:mt-2 ui:text-sm ui:text-neutral-black ui:font-medium'>
                {isUploading
                  ? 'Mengunggah...'
                  : isDragActive
                    ? locale === 'id'
                      ? 'Lepaskan file disini'
                      : 'Drop file here'
                    : locale === 'id'
                      ? 'Drag gambar kesini'
                      : 'Drag image here'}
              </p>

              <p className='ui:text-xs ui:text-grey-100'>
                {locale === 'id'
                  ? `Atau klik disini (maksimal ${maxFileSizeMB}MB, format:`
                  : `Or click here (maximum ${maxFileSizeMB}MB, format:`}{' '}
                {acceptedFileTypes.map((t) => t.split('/')[1]).join(', ')})
              </p>
            </div>
          </label>

          <Input
            id='dropzone-file'
            accept={acceptedFileTypes.join(', ')}
            type='file'
            className='ui:hidden'
            onChange={(e) => handleImage(e.target.files)}
            disabled={(disabled ?? false) || isUploading}
            aria-label={locale === 'id' ? 'Upload gambar profil' : 'Upload profile image'}
            {...(({ size: _size, ...rest }) => rest)(getInputProps())}
          />
        </div>

        {error && (
          <div className='ui:mt-4 ui:text-sm ui:text-error' role='alert'>
            {error}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
